import {
  Avatar,
  Box,
  Card,
  CardContent,
  Link,
  Typography,
} from "@mui/material";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Legend, Tooltip } from "chart.js";
import { formatDistance } from "date-fns";

import { Scrollbar } from "react-scrollbars-custom";

import { Tooltip as ChartToolTip } from "@mui/material";
import { ContributorsCardProps } from "../../lib/githubHooks";
import { InfoOutlined } from "@mui/icons-material";

ChartJS.register(ArcElement, Legend, Tooltip);

export interface TopContributorComponentProps {
  avatarUrl: string;
  username: string;
  commits: number;
}

const TopContributor = ({
  avatarUrl,
  username,
  commits,
}: TopContributorComponentProps) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    width="100%"
    py={1}
  >
    <Box display="flex" alignItems="center">
      <Avatar
        src={avatarUrl}
        alt={username}
        sx={{ width: 32, height: 32, mr: 1 }}
      />
      <Link
        href={`https://github.com/${username}`}
        underline="hover"
        variant="body2"
        color="text.primary"
      >
        {username}
      </Link>
    </Box>
    <Typography
      variant="body2"
      color="text.secondary"
      style={{ marginRight: 30 }}
    >
      {commits.toLocaleString()} commits
    </Typography>
  </Box>
);

const CommitItem = ({ commit }: any) => {
  const {
    author: { avatarUrl, user },
    message,
    committedDate,
    oid,
    repository: { nameWithOwner },
  } = commit;

  const username = user?.login || "unknown";
  const shortSha = oid.substring(0, 7);

  const date = new Date();

  const now_utc = date.toISOString().slice(0, -5);

  const formattedDate = formatDistance(
    committedDate.replace("Z", "") ?? 0,
    now_utc,
    { addSuffix: true }
  ).toString();

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="flex-start"
      gap={2}
      sx={{ minHeight: 60 }}
    >
      <Avatar
        src={avatarUrl}
        alt={username}
        sx={{ width: 36, height: 36, mt: 0.5 }}
      />

      <Box flex={1}>
        <Typography variant="body1" fontWeight="medium">
          {message}
        </Typography>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          mt={0.5}
        >
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Link
              href={`https://github.com/${nameWithOwner}`}
              target="_blank"
              rel="noopener"
              variant="body2"
            >
              {nameWithOwner}
            </Link>
            <Typography variant="body2" color="text.secondary">
              ·
            </Typography>
            <Link
              href={`https://github.com/${nameWithOwner}/commit/${oid}`}
              target="_blank"
              rel="noopener"
              variant="body2"
            >
              {shortSha}
            </Link>
            {user?.login && (
              <>
                <Typography variant="body2" color="text.secondary">
                  ·
                </Typography>
                <Link
                  href={`https://github.com/${user.login}`}
                  target="_blank"
                  rel="noopener"
                  variant="body2"
                >
                  {user.login}
                </Link>
              </>
            )}
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", marginRight: 5 }}
          >
            {formattedDate}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Main contributors card
const cardComponent = ({ title, data, type, info }: ContributorsCardProps) => {
  const newCount = 0;
  const activeCount = data - newCount;

  const ringData = {
    labels: ["New", "Active"],
    datasets: [
      {
        data: [newCount, activeCount],
        backgroundColor: ["#d0cfff", "rgb(159, 204, 46)"],
        borderWidth: 0,
        cutout: "95%",
      },
    ],
  };

  const ringOptions = {
    plugins: {
      legend: {
        display: false,
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          mt: 2,
          boxShadow: "0 8px 10px #00000040",
          borderRadius: 4,
          p: 2,
          mx: "auto",
          boxSizing: "border-box",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {title}
          </Typography>
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
          >
            <ChartToolTip
              title={
                <>
                  <Typography fontSize={16} mb={1}>
                    {info.desc}
                  </Typography>
                  <Typography fontSize={16}>{info.time}</Typography>
                </>
              }
            >
              <InfoOutlined fontSize="small" color="action" />
            </ChartToolTip>
          </Typography>
        </Box>

        {type === "Ring" ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ height: 460 }}
          >
            <Box sx={{ width: "100%", minHeight: 420, position: "relative" }}>
              <Doughnut data={ringData} options={ringOptions} />
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {data}
                </Typography>
              </Box>
            </Box>
            {/* <Box mt={4} width="100%">
            <Divider sx={{ mb: 5, borderBottomWidth: 3 }} />
            <Box display="flex" justifyContent="center" alignItems="center" gap={4}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#d0cfff' }} />
                <Typography variant="caption">New</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#0000ff' }} />
                <Typography variant="caption">Active</Typography>
              </Box>
            </Box>
          </Box> */}
          </Box>
        ) : type === "ListCommits" ? (
          <Card sx={{ border: "none", boxShadow: "none" }}>
            <CardContent sx={{ p: 2, maxHeight: 420, overflowY: "auto" }}>
              <Scrollbar
                style={{ height: 420 }}
                trackYProps={{
                  style: { backgroundColor: "transparent" },
                }}
                thumbYProps={{
                  style: {
                    backgroundColor: "rgba(54, 51, 51, 0.2)",
                    borderRadius: "6px",
                    width: "6px",
                  },
                }}
              >
                <Box display="flex" flexDirection="column" gap={4}>
                  {Array.isArray(data) &&
                    data.map((commit, idx) => (
                      <CommitItem key={idx} commit={commit} />
                    ))}
                </Box>
              </Scrollbar>
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ border: "none", boxShadow: "none", scrollbar: "none" }}>
            <CardContent
              sx={{
                height: 420,
                overflowY: "auto",
                scrollbar: "none",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <Scrollbar
                style={{ height: 420 }}
                trackYProps={{
                  style: { backgroundColor: "transparent" },
                }}
                thumbYProps={{
                  style: {
                    backgroundColor: "rgba(54, 51, 51, 0.2)",
                    borderRadius: "6px",
                    width: "6px",
                  },
                }}
              >
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                  {data &&
                    Object.entries(data).map(([username, contributorData]) => {
                      const { avatarUrl, commits } = contributorData as {
                        avatarUrl: string;
                        commits: number;
                      };
                      return (
                        <TopContributor
                          key={username}
                          avatarUrl={avatarUrl}
                          username={username}
                          commits={commits}
                        />
                      );
                    })}
                </Box>
              </Scrollbar>
            </CardContent>
          </Card>
        )}
      </Box>
    </>
  );
};

export default cardComponent;
